import React, { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import {
  Visibility as ViewIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
} from '@material-ui/icons'
import {
  Button,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import dayjs from 'dayjs'
import {
  AdminPageWrapper,
  AdminSearchBar,
  AdminTable,
  AdminTableColumn,
} from '../AdminComponents'
import { AiCoachingIntervention } from '../../../../Generated/graphql-types'
import {
  GET_AI_COACHING_INTERVENTIONS,
  TRIGGER_INTERVENTION,
} from '../../../../Apollo/Queries/AiCoachingInterventions'
import {
  UserInfoContainer,
  UserName,
  UserEmail,
  TruncatedMessage,
  QuickTriggerSection,
  QuickTriggerTitle,
  ButtonContainer,
} from './AiCoachingAdmin.styled'

// Local interface for AI Coaching Admin search filters
interface AiCoachingSearchFilters {
  userId?: string
  userEmail?: string
  identifier?: string
  status?: string
}

export const AiCoachingAdmin: React.FC = () => {
  const history = useHistory()
  const [searchFilters, setSearchFilters] = useState<AiCoachingSearchFilters>(
    {}
  )
  const [currentPage, setCurrentPage] = useState(0)
  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [userDialogInput, setUserDialogInput] = useState('')
  const [selectedIntervention, setSelectedIntervention] = useState<string>('')
  const [forceIntervention, setForceIntervention] = useState(false)
  const [isTriggering, setIsTriggering] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const pageSize = 20

  // Build query variables
  const queryVariables = useMemo(
    () => ({
      userId: searchFilters.userId
        ? parseInt(searchFilters.userId, 10)
        : undefined,
      userEmail: searchFilters.userEmail || undefined,
      identifier: searchFilters.identifier || undefined,
      shouldIntervene: (() => {
        if (searchFilters.status === 'intervened') return true
        if (searchFilters.status === 'skipped') return false
        return undefined
      })(),
      limit: pageSize,
      offset: currentPage * pageSize,
    }),
    [searchFilters, currentPage]
  )

  const { data, loading, error, refetch } = useQuery<{
    aiCoachingInterventions: {
      total: number
      interventions: AiCoachingIntervention[]
    }
  }>(GET_AI_COACHING_INTERVENTIONS, {
    variables: queryVariables,
  })

  const [triggerIntervention] = useMutation(TRIGGER_INTERVENTION)

  const searchFields = [
    {
      key: 'userId',
      label: 'User ID',
      placeholder: 'Enter user ID',
      type: 'number' as const,
    },
    {
      key: 'userEmail',
      label: 'User Email',
      placeholder: 'Enter user email',
    },
    {
      key: 'identifier',
      label: 'Intervention Type',
      placeholder: 'e.g., PostCoachingSession',
    },
    {
      key: 'status',
      label: 'Status',
      placeholder: 'intervened or skipped',
    },
  ]

  const columns: AdminTableColumn<AiCoachingIntervention>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'user',
      label: 'User',
      render: (_, row) =>
        row.user ? (
          <UserInfoContainer>
            <UserName>{`${row.user.firstName || ''} ${
              row.user.lastName || ''
            }`}</UserName>
            <UserEmail>{row.user.email || ''}</UserEmail>
          </UserInfoContainer>
        ) : (
          'Unknown User'
        ),
    },
    {
      key: 'identifier',
      label: 'Type',
      render: (value) => value || 'Unknown',
    },
    {
      key: 'version',
      label: 'Version',
      render: (value) => value || '-',
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (value) =>
        value ? dayjs(value).format('MMM D, YYYY h:mm A') : '-',
    },
    {
      key: 'message',
      label: 'Message',
      render: (value) =>
        value ? <TruncatedMessage>{value}</TruncatedMessage> : '-',
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => {
        if (!value) return '-'
        const statusColors: Record<string, string> = {
          NEW: '#1976d2',
          EMAILED: '#0288d1',
          POSTED: '#388e3c',
          POSTED_AND_EMAILED: '#2e7d32',
          DECLINED: '#d32f2f',
        }
        return (
          <Typography
            variant='caption'
            sx={{
              color: statusColors[value as string] || '#666',
              fontWeight: 'medium',
            }}
          >
            {(value as string).replace(/_/g, ' ')}
          </Typography>
        )
      },
    },
    {
      key: 'coachRating',
      label: 'Rating',
      render: (value) => {
        if (!value) return '-'
        return (
          <Typography
            variant='caption'
            sx={{
              color: value === 'GOOD' ? '#4caf50' : '#f44336',
              fontWeight: 'medium',
            }}
          >
            {value}
          </Typography>
        )
      },
    },
    {
      key: 'claimedBy',
      label: 'Coach',
      render: (value) => (value ? `${value.firstName} ${value.lastName}` : '-'),
    },
  ]

  const handleRowClick = (row: AiCoachingIntervention) => {
    history.push(`/internal/self-serve/ai-coaching-admin/${row.id}`)
  }

  const handleTriggerIntervention = async (
    identifier: string,
    userId: number,
    force?: boolean
  ) => {
    setIsTriggering(true)
    try {
      const result = await triggerIntervention({
        variables: { identifier, userId, force: force || false },
      })

      if (result.data?.triggerAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message:
            result.data.triggerAiCoachingIntervention.message ||
            'Intervention triggered successfully!',
          severity: 'success',
        })
      } else {
        setSnackbar({
          open: true,
          message:
            result.data?.triggerAiCoachingIntervention?.message ||
            'Failed to trigger intervention',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    } finally {
      setIsTriggering(false)
    }
  }

  const handleOpenUserDialog = () => {
    setUserDialogOpen(true)
    setUserDialogInput('')
    setSelectedIntervention('')
    setForceIntervention(false)
  }

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false)
    setUserDialogInput('')
    setSelectedIntervention('')
    setForceIntervention(false)
  }

  const handleTriggerFromDialog = async () => {
    const userId = parseInt(userDialogInput, 10)
    if (!Number.isNaN(userId) && userId > 0 && selectedIntervention) {
      await handleTriggerIntervention(
        selectedIntervention,
        userId,
        forceIntervention
      )
      handleCloseUserDialog()
    }
  }

  const actions = [
    {
      icon: <ViewIcon />,
      tooltip: 'View details',
      onClick: (row: AiCoachingIntervention) => handleRowClick(row),
    },
  ]

  // Get unique intervention types for quick trigger buttons
  const availableInterventions = [
    {
      id: 'PostCoachingSession',
      name: 'Post Coaching Session',
      description: 'Send follow-up after coaching calls',
    },
    {
      id: 'AbandonedGoals',
      name: 'Abandoned Goals',
      description: 'Re-engage users with stale goals',
    },
    {
      id: 'UnUpdatedGoals',
      name: 'Un-Updated Goals',
      description: 'Remind users to update goals with no recent activity',
    },
    {
      id: 'TemplateDownload',
      name: 'Template Download',
      description: 'Follow up with users after template downloads',
    },
  ]

  return (
    <AdminPageWrapper
      title='AI Coaching Interventions'
      subtitle='View and test AI coaching interventions for users'
      actions={
        <Box display='flex' gap={2}>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleOpenUserDialog}
          >
            Test Intervention
          </Button>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        </Box>
      }
    >
      <AdminSearchBar
        fields={searchFields}
        onSearch={(filters: AiCoachingSearchFilters) => {
          setSearchFilters(filters)
          setCurrentPage(0)
        }}
      />

      {/* Quick trigger section for testing */}
      {searchFilters.userId && (
        <QuickTriggerSection>
          <QuickTriggerTitle>
            Test Interventions for User #{searchFilters.userId}
          </QuickTriggerTitle>
          <ButtonContainer>
            {availableInterventions.map((intervention) => (
              <Button
                key={intervention.id}
                variant='contained'
                size='small'
                startIcon={<PlayIcon />}
                onClick={() =>
                  handleTriggerIntervention(
                    intervention.id,
                    parseInt(searchFilters.userId || '0', 10)
                  )
                }
                disabled={isTriggering}
              >
                {isTriggering
                  ? 'Triggering...'
                  : `Trigger ${intervention.name}`}
              </Button>
            ))}
          </ButtonContainer>
        </QuickTriggerSection>
      )}

      <AdminTable
        columns={columns}
        data={data?.aiCoachingInterventions.interventions || []}
        loading={loading}
        onRowClick={handleRowClick}
        actions={actions}
        emptyMessage={
          error
            ? `Error loading interventions: ${error.message}`
            : 'No interventions found'
        }
        pageSize={pageSize}
        totalCount={data?.aiCoachingInterventions.total || 0}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={userDialogOpen}
        onClose={handleCloseUserDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Test AI Coaching Intervention</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Select a user and intervention type to trigger a test. The system
            will check if the user qualifies for the intervention and process it
            automatically.
          </Typography>
          <TextField
            autoFocus
            margin='dense'
            label='User ID'
            type='number'
            fullWidth
            variant='outlined'
            value={userDialogInput}
            onChange={(e) => setUserDialogInput(e.target.value)}
            helperText='Enter the numeric user ID (e.g., 123)'
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant='outlined'>
            <InputLabel>Intervention Type</InputLabel>
            <Select
              value={selectedIntervention}
              onChange={(e) => setSelectedIntervention(e.target.value)}
              label='Intervention Type'
            >
              <MenuItem value=''>Select an intervention</MenuItem>
              {availableInterventions.map((intervention) => (
                <MenuItem key={intervention.id} value={intervention.id}>
                  <Box>
                    <Typography variant='body1'>{intervention.name}</Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {intervention.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={forceIntervention}
                onChange={(e) => setForceIntervention(e.target.checked)}
                color='primary'
              />
            }
            label='Force intervention (bypass criteria check)'
            sx={{ mt: 2 }}
          />
          {forceIntervention && (
            <Typography
              variant='caption'
              color='warning.main'
              sx={{ mt: 1, display: 'block' }}
            >
              Warning: Forcing will run the intervention even if the user
              doesn&apos;t meet the normal criteria. This is useful for testing
              but should not be used in production.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserDialog}>Cancel</Button>
          <Button
            onClick={handleTriggerFromDialog}
            variant='contained'
            disabled={
              !userDialogInput ||
              Number.isNaN(parseInt(userDialogInput, 10)) ||
              !selectedIntervention ||
              isTriggering
            }
          >
            {isTriggering ? 'Triggering...' : 'Trigger Intervention'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}
